import { Dialog, DialogContent } from "@mui/material";
import { EdmErrorResponse } from "App.types";
import { useEdmPermissions, usePollDocumentGenerationTaskProgress, useRecordHistoryContext } from "misc/customHooks";
import { createInvoices } from "misc/edmClient";
import { isExternalAccountingActive } from "misc/permissionHelpers";
import ClosableDialogTitle from "molecules/ClosableDialogTitle";
import { GenerateDocumentProgressType } from "organisms/GenerateDocumentDialogButton/GenerateDocumentDialogButton.types";
import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { GeneratedDocumentRecordType } from "pages/DocumentTemplate/DocumentTemplate.types";
import { EventRecordType } from "pages/Event/Event.types";
import { InvoiceResponse } from "pages/Invoice/Invoice.types";
import { AttendeeRecordType, ContactInfoType } from "pages/SharedRecords.types";
import { useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import { FieldValues, SubmitHandler } from "react-hook-form";

import CreateInvoiceForm from "./CreateInvoiceForm";
import CreateInvoiceOverview from "./CreateInvoiceOverview";

export type InvoiceFormData = Partial<InvoiceRecordType> & { override_prices: boolean; override_contact_info: boolean };

type CreateInvoiceDialogContentPropsType = {
  event: EventRecordType;
  bookingsWithAttendees: BookingRecordType<AttendeeRecordType>[];
  parentInvoice?: InvoiceRecordType;
  handleSubmit: SubmitHandler<FieldValues>;
  invoiceResponses?: InvoiceResponse[];
  documentTaskProgress: GenerateDocumentProgressType;
  generatedDocuments: GeneratedDocumentRecordType[];
};

function CreateInvoiceDialogContent(props: CreateInvoiceDialogContentPropsType) {
  const {
    documentTaskProgress,
    invoiceResponses,
    handleSubmit,
    bookingsWithAttendees,
    parentInvoice,
    event,
    generatedDocuments,
  } = props;
  const { manageAccounting } = useEdmPermissions();

  if (invoiceResponses) {
    return (
      <DialogContent>
        <CreateInvoiceOverview
          showExternalAccountingFeature={manageAccounting && isExternalAccountingActive()}
          event={event}
          invoiceResponses={invoiceResponses}
          bookingsWithAttendees={bookingsWithAttendees}
          documentTaskProgress={documentTaskProgress}
          generatedDocuments={generatedDocuments}
        />
      </DialogContent>
    );
  } else {
    return (
      <DialogContent>
        <CreateInvoiceForm
          parentInvoice={parentInvoice}
          handleSubmit={handleSubmit}
          bookingsWithAttendees={bookingsWithAttendees}
          event={event}
        />
      </DialogContent>
    );
  }
}

type CreateInvoiceDialogPropsType = {
  event: EventRecordType;
  bookingsWithAttendees: BookingRecordType<AttendeeRecordType>[];
  dialogIsOpen: boolean;
  setDialogIsOpen: (value: boolean) => void;
  label: string;
  refreshBooking: () => void;
  parentInvoice?: InvoiceRecordType;
};

export default function CreateInvoiceDialog(props: CreateInvoiceDialogPropsType) {
  const { event, bookingsWithAttendees, dialogIsOpen, setDialogIsOpen, label, refreshBooking, parentInvoice } = props;
  const [invoiceResponses, setInvoiceResponses] = useState<InvoiceResponse[]>();
  const { initiateTaskProgressPolling, progress, generatedDocuments } = usePollDocumentGenerationTaskProgress();
  const refresh = useRefresh();
  const { refetchHistory } = useRecordHistoryContext();
  const notify = useNotify();

  async function handleSubmit(values: FieldValues) {
    if (!event) return;

    const formData = values as InvoiceFormData;

    setInvoiceResponses([]);

    const data = bookingsWithAttendees.map((booking) => {
      const { id, price } = booking;
      const {
        debit_number,
        title,
        first_name,
        last_name,
        invoice_dispatch_contact_info,
        invoice_contact_info,
        main_contact_info,
      } = booking.attendee;

      const data = {
        booking: id,
        debit_number,
        first_name,
        invoice_contact_info,
        invoice_dispatch_contact_info,
        kind: formData.kind,
        last_name,
        main_contact_info,
        name: formData.name,
        service_period: formData.service_period,
        price: formData.parent ? formData.price : price,
        parent: formData.parent,
        template: formData.template,
        title,
      };

      if (formData.override_prices) {
        data.price = formData.price;
      }

      if (formData.override_contact_info) {
        data.invoice_contact_info = formData.invoice_contact_info as ContactInfoType;
        data.title = formData.title as string;
        data.first_name = formData.first_name as string;
        data.last_name = formData.last_name as string;
      }

      return data;
    });

    try {
      const responses = await createInvoices(data);
      setInvoiceResponses(responses);

      initiateTaskProgressPolling(responses[0].document_task_id);

      if (refreshBooking) refreshBooking();
      refetchHistory();
      refresh();
    } catch (e) {
      const error = e as EdmErrorResponse;
      const details = error.body.errors?.invoice || null;
      const message = details ? details : error.message;
      notify(message, { type: "error" });
      closeDialog();
    }
  }

  function closeDialog(_e?: unknown, reason?: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    setDialogIsOpen(false);
    setInvoiceResponses(undefined);
    // TODO[formErrors]
    // dispatch(setFormErrors({ invoices: {} }));
  }

  return (
    <Dialog open={dialogIsOpen} onClose={closeDialog}>
      <ClosableDialogTitle closeDialog={closeDialog}>{label}</ClosableDialogTitle>

      {dialogIsOpen && (
        <CreateInvoiceDialogContent
          invoiceResponses={invoiceResponses}
          documentTaskProgress={progress}
          handleSubmit={handleSubmit}
          bookingsWithAttendees={bookingsWithAttendees}
          parentInvoice={parentInvoice}
          event={event}
          generatedDocuments={generatedDocuments}
        />
      )}
    </Dialog>
  );
}
